import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Camera, Menu, X, LogIn, LogOut, User,
  Instagram, Facebook, Youtube, Twitter, Linkedin, MessageCircle, Mail, Eye,
} from "@/lib/icons";
import { api, resolvePhotoUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { trackWebsiteVisit } from "@/lib/visitor";
import BrandLogo from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";
import ProfileLink from "@/components/ProfileLink";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/gomore", label: "GoMore" },
  { to: "/register", label: "Register" },
  { to: "/add-friend", label: "Add Friend" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const SOCIAL_ICONS = {
  Instagram, Facebook, YouTube: Youtube, Twitter, LinkedIn: Linkedin, WhatsApp: MessageCircle,
};

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [websiteViews, setWebsiteViews] = useState(null);
  const { user, logout } = useAuth();
  const { settings: ctxSettings } = useSettings();
  const nav = useNavigate();

  useEffect(() => { api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => setSettings({})); }, []);
  useEffect(() => { trackWebsiteVisit().catch(() => {}); }, []);
  useEffect(() => {
    api.get("/public/analytics/total-views")
      .then((r) => setWebsiteViews(r.data.total_page_views))
      .catch(() => {});
  }, []);

  const brandName = (ctxSettings?.website_name) || settings?.website_name || "PRONET";
  const logoUrl = resolvePhotoUrl(ctxSettings?.logo_url || settings?.logo_url);

  const doLogout = async () => { await logout(); setOpen(false); nav("/"); };

  const AuthButton = ({ mobile = false }) => {
    if (!user) return (
      <Link to="/login" onClick={() => setOpen(false)}
        data-testid={mobile ? "mobile-nav-login" : "nav-login"}
        className={`btn-glow inline-flex items-center gap-1.5 rounded-full ${mobile ? "px-4 py-2.5" : "px-5 py-2"} text-sm font-semibold text-white`}>
        <LogIn size={13} /> Sign in
      </Link>
    );
    if (user.role === "member") return (
      <div className={`flex ${mobile ? "flex-col gap-2" : "items-center gap-2"}`}>
        <ProfileLink to="/account" onClick={() => setOpen(false)} data-testid={mobile ? "mobile-nav-account" : "nav-account"}
          className={`btn-ghost inline-flex items-center gap-1.5 rounded-full ${mobile ? "px-4 py-2.5 justify-center" : "px-4 py-2"} text-sm font-semibold text-white`}>
          <User size={13} /> {user.full_name?.split(" ")[0] || "Account"}
        </ProfileLink>
        <button onClick={doLogout} data-testid={mobile ? "mobile-nav-signout" : "nav-signout"}
          className={`inline-flex items-center gap-1.5 rounded-full ${mobile ? "px-4 py-2.5 justify-center border border-red-500/30 bg-red-500/10 text-red-300" : "px-3 py-2 text-white/60 hover:text-red-300"} text-sm font-medium`}>
          <LogOut size={13} /> Sign out
        </button>
      </div>
    );
    return (
      <Link to="/admin" onClick={() => setOpen(false)}
        className={`btn-glow inline-flex items-center gap-1.5 rounded-full ${mobile ? "px-4 py-2.5" : "px-5 py-2"} text-sm font-semibold text-white`}>
        Admin Panel
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex flex-col text-white">
      <header className="glass-nav sticky top-0 z-40">
        <div className="container-wide flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="brand-logo">
            <BrandLogo size={36} testid="header-brand" />
            <div className="leading-tight">
              <div className="font-display font-semibold text-lg tracking-wide">{brandName}</div>
              <div className="hidden sm:block overline text-[9px] text-fuchsia-300/80 -mt-0.5">Photographers Network</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `px-3.5 py-2 text-sm rounded-md transition-colors ${
                  isActive ? "text-fuchsia-300" : "text-white/70 hover:text-white"
                }`}>
                {n.label}
              </NavLink>
            ))}
            <div className="ml-3 flex items-center gap-2">
              <ThemeToggle />
              <AuthButton />
            </div>
          </nav>

          <button className="lg:hidden p-2 rounded-md hover:bg-white/5"
            onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/5 bg-ink-950/95">
            <div className="container-wide py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) => `px-3 py-2.5 rounded-md text-sm ${
                    isActive ? "bg-white/10 text-fuchsia-300" : "text-white/70"
                  }`}>
                  {n.label}
                </NavLink>
              ))}
              <div className="mt-3 pt-3 border-t border-white/5 flex flex-col gap-3">
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs font-medium text-white/55">Appearance</span>
                  <ThemeToggle showLabel />
                </div>
                <AuthButton mobile />
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <Footer settings={settings} websiteViews={websiteViews} />
    </div>
  );
}

function Footer({ settings, websiteViews }) {
  const { settings: ctx } = useSettings();
  const merged = { ...ctx, ...(settings || {}) };
  const social = (merged.social_links || []).filter((s) => s.url || s.handle);
  const copyright = (merged.footer && merged.footer.copyright) || "© KANGRUPHOTO 2019";
  const tagline = (merged.footer && merged.footer.tagline) || "Find photographers, videographers and creative professionals across India.";
  const logoUrl = resolvePhotoUrl(merged.logo_url);

  return (
    <footer className="site-footer relative mt-24 border-t border-white/5">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[500px] h-[500px] -left-40 top-10 opacity-25" />
        <div className="blob blob-magenta w-[420px] h-[420px] right-0 -bottom-20 opacity-20" />
      </div>

      <div className="container-wide py-14 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <BrandLogo size={40} testid="footer-brand-logo" />
              <div className="font-display text-2xl tracking-wider" data-testid="footer-brand">{merged.website_name || "PRONET"}</div>
            </div>
            <p className="mt-4 text-sm text-white/55 max-w-md leading-relaxed" data-testid="footer-tagline">
              {tagline}
            </p>
            {social.length > 0 && (
              <div className="mt-6 flex items-center gap-2">
                {social.map((s) => {
                  const Icon = SOCIAL_ICONS[s.name] || Mail;
                  return (
                    <a key={s.name} href={s.url || "#"} target="_blank" rel="noreferrer" title={s.name}
                      data-testid={`footer-social-${s.name.toLowerCase()}`}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full glass hover:bg-white/10 transition-colors text-white/70 hover:text-fuchsia-300">
                      <Icon size={16} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Index */}
          <div>
            <div className="overline text-fuchsia-300/80 mb-4">Index</div>
            <ul className="space-y-2.5 text-sm">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} data-testid={`footer-nav-${n.label.toLowerCase()}`}
                    className="text-white/60 hover:text-fuchsia-300 transition-colors">{n.label}</Link>
                </li>
              ))}
              <li><Link to="/login" data-testid="footer-nav-login" className="text-white/60 hover:text-fuchsia-300 transition-colors">Member Login</Link></li>
              <li><Link to="/how-to-register" data-testid="footer-nav-how-to-register" className="text-white/60 hover:text-fuchsia-300 transition-colors">How to Register</Link></li>
              <li><Link to="/verification" data-testid="footer-nav-verification" className="text-white/60 hover:text-fuchsia-300 transition-colors">Verification</Link></li>
              <li><Link to="/privacy" data-testid="footer-nav-privacy" className="text-white/60 hover:text-fuchsia-300 transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Follow */}
          <div>
            <div className="overline text-fuchsia-300/80 mb-4">Follow</div>
            <ul className="space-y-2.5 text-sm">
              {(settings?.social_links || []).map((s) => {
                const Icon = SOCIAL_ICONS[s.name] || Mail;
                const href = s.url || "#";
                return (
                  <li key={s.name}>
                    <a href={href} target="_blank" rel="noreferrer"
                      data-testid={`footer-follow-${s.name.toLowerCase()}`}
                      className="inline-flex items-center gap-2 text-white/60 hover:text-fuchsia-300 transition-colors">
                      <Icon size={13} /> {s.name}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-[0.4em] text-white/40" data-testid="footer-copyright">{copyright}</div>
          <div
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/55"
            data-testid="footer-visitor-count"
          >
            <Eye size={13} className="text-fuchsia-300" />
            <span className="tabular-nums font-medium text-white/80">
              {websiteViews == null ? "10,000+" : websiteViews.toLocaleString()}
            </span>
            <span>website views</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
